import type { CarrierTask, CarrierTrip } from "@/api/carrier";

export type DispatchWindowFilter =
  | { kind: "window"; startTime: string; endTime: string }
  | { kind: "overdue" }
  | null;

interface DispatchTaskFilter {
  date: string;
  window: DispatchWindowFilter;
  now?: Date;
}

function taskDate(task: CarrierTask): string | null {
  if (task.scheduledWindow?.date) return task.scheduledWindow.date;
  return task.scheduledDate?.slice(0, 10) ?? null;
}

export function dispatchDateKey(
  date: Date,
  timeZone?: string | null
): string | null {
  if (!timeZone) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const value = (part: "year" | "month" | "day") =>
      parts.find((candidate) => candidate.type === part)?.value;
    const year = value("year");
    const month = value("month");
    const day = value("day");
    if (year && month && day) return `${year}-${month}-${day}`;
  } catch {
    return null;
  }
  return null;
}

function scheduledEnd(task: CarrierTask): Date | null {
  if (!task.scheduledWindow) return null;
  const exactEnd = new Date(task.scheduledWindow.endAt);
  return Number.isNaN(exactEnd.getTime()) ? null : exactEnd;
}

export function isDispatchTaskOverdue(
  task: CarrierTask,
  now = new Date()
): boolean {
  if (["COMPLETED", "DONE", "CANCELLED", "CANCELED"].includes(task.status)) {
    return false;
  }
  if (task.scheduledWindow) {
    const end = scheduledEnd(task);
    return end ? end.getTime() < now.getTime() : false;
  }
  const scheduledDate = taskDate(task);
  const currentOperationalDate = dispatchDateKey(now, task.schedulingTimeZone);
  return Boolean(
    scheduledDate &&
      currentOperationalDate &&
      scheduledDate < currentOperationalDate
  );
}

export function filterDispatchTasks(
  tasks: CarrierTask[],
  { date, window, now = new Date() }: DispatchTaskFilter
): CarrierTask[] {
  return tasks.filter((task) => {
    if (isDispatchTaskOverdue(task, now)) return true;
    if (window?.kind === "overdue") return false;
    const scheduledDate = taskDate(task);
    if (scheduledDate && scheduledDate !== date) return false;
    if (!window) return true;
    if (!task.scheduledWindow) return false;
    return (
      task.scheduledWindow.startTime === window.startTime &&
      task.scheduledWindow.endTime === window.endTime
    );
  });
}

export function dispatchTaskScheduleLabel(
  task: CarrierTask,
  now = new Date()
): string {
  if (task.scheduledWindow) {
    return `${task.scheduledWindow.date} · ${task.scheduledWindow.startTime}–${task.scheduledWindow.endTime}`;
  }
  const scheduledDate = task.scheduledDate?.slice(0, 10);
  if (!scheduledDate) return "Unscheduled · configure operational timing";
  return `${isDispatchTaskOverdue(task, now) ? "Overdue" : "Scheduled"} · ${scheduledDate}`;
}

export function dispatchWindowFilterValue(
  filter: DispatchWindowFilter
): string {
  if (!filter) return "all";
  if (filter.kind === "overdue") return "overdue";
  return `${filter.startTime}|${filter.endTime}`;
}

export function parseDispatchWindowFilter(value: string): DispatchWindowFilter {
  if (value === "all") return null;
  if (value === "overdue") return { kind: "overdue" };
  const [startTime, endTime, ...rest] = value.split("|");
  return startTime && endTime && rest.length === 0
    ? { kind: "window", startTime, endTime }
    : null;
}

export interface DispatchCapacityLoad {
  load: number;
  capacity: number;
  unit: "packages" | "kg" | "cm³" | "m³" | "L";
}

function positiveNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : null;
}

function snapshotNumber(
  snapshot: unknown,
  key:
    | "packageCount"
    | "totalWeightGrams"
    | "totalVolumeCm3"
    | "totalVolumeM3"
    | "totalVolumeLiters"
): number | null {
  if (!snapshot || typeof snapshot !== "object" || Array.isArray(snapshot)) {
    return null;
  }
  const value = (snapshot as Record<string, unknown>)[key];
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

function completeTaskLoad(
  trip: CarrierTrip,
  key: Parameters<typeof snapshotNumber>[1]
): number | null {
  const tasks = trip.tasks ?? [];
  if (tasks.length === 0) return 0;
  const values = tasks.map((task) =>
    snapshotNumber(task.shipmentSnapshot, key)
  );
  return values.every((value): value is number => value != null)
    ? values.reduce((sum, value) => sum + value, 0)
    : null;
}

/** Compare load only when Vehicle capacity and every Task use the same unit. */
export function dispatchCapacityLoad(
  trip: CarrierTrip
): DispatchCapacityLoad | null {
  const capacity = trip.vehicle?.capacity;
  if (!capacity || typeof capacity !== "object" || Array.isArray(capacity)) {
    return null;
  }
  const record = capacity as Record<string, unknown>;
  const packageCapacity =
    positiveNumber(record.parcels) ?? positiveNumber(record.packages);
  if (packageCapacity != null) {
    const load = completeTaskLoad(trip, "packageCount");
    if (load != null) {
      return { load, capacity: packageCapacity, unit: "packages" };
    }
  }

  const weightCapacity = positiveNumber(record.weightKg);
  if (weightCapacity != null) {
    const grams = completeTaskLoad(trip, "totalWeightGrams");
    if (grams != null) {
      return { load: grams / 1000, capacity: weightCapacity, unit: "kg" };
    }
  }

  const volumeCandidates = [
    ["volumeCm3", "totalVolumeCm3", "cm³"],
    ["volumeM3", "totalVolumeM3", "m³"],
    ["volumeLiters", "totalVolumeLiters", "L"],
  ] as const;
  for (const [capacityKey, snapshotKey, unit] of volumeCandidates) {
    const volumeCapacity = positiveNumber(record[capacityKey]);
    if (volumeCapacity == null) continue;
    const load = completeTaskLoad(trip, snapshotKey);
    if (load != null) return { load, capacity: volumeCapacity, unit };
  }
  return null;
}
